// src/components/ChatbotPage.js
import React, { useState, useCallback, useEffect, useContext } from 'react';
import ClaudeChat from './ClaudeChat';
import emotions from '../utils/emotions';
import { ReactComponent as HeartIcon } from '../assets/heart.svg';
import { ReactComponent as FlagIcon } from '../assets/flag.svg';
import { ReactComponent as PlusIcon } from '../assets/plus.svg';
import './ChatbotPage.css';
import ReactConfetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import FaqButton from './FaqButton';
import Popup from './Popup';
import AnimatedQuestion from './AnimatedQuestion';

const ChatbotPage = ({ userInfo, emotionHistory, setEmotionHistory }) => {
  const { firstName } = userInfo;
  const [messages, setMessages] = useState([]);
  const [resultNumber, setResultNumber] = useState(null);
  const [minimizedShapes, setMinimizedShapes] = useState([]);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isInitialConversationComplete, setIsInitialConversationComplete] = useState(false);
  
  const [showFlagPopup, setShowFlagPopup] = useState(false);
  const [showPlusPopup, setShowPlusPopup] = useState(false);
  const [flagFeedback, setFlagFeedback] = useState('');
  const [plusFeedback, setPlusFeedback] = useState({ name: '', description: '' });
  const [isLiked, setIsLiked] = useState(false);
  const [uniqueEmotions, setUniqueEmotions] = useState(new Set([]));
  const [isLoadingEmotions, setIsLoadingEmotions] = useState(true);
  const { width, height } = useWindowSize();
  const [isShaking, setIsShaking] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [shouldAnimateCounter, setShouldAnimateCounter] = useState(false);
  const [isBlurred, setIsBlurred] = useState(true);
  const [showDeletionWarning, setShowDeletionWarning] = useState(false);
  const [emotionToDelete, setEmotionToDelete] = useState(null);
  const [isOverlayFromHistory, setIsOverlayFromHistory] = useState(false);
  const [highlightedShape, setHighlightedShape] = useState(null);
  const [lastTap, setLastTap] = useState(0);
  const [touchTimer, setTouchTimer] = useState(null);
  const [showDiscarded, setShowDiscarded] = useState(false);

  const toggleBlur = () => {
    setIsBlurred((prev) => !prev);
  };

  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return `Good morning, ${firstName}`;
    else if (currentHour < 18) return `Good afternoon, ${firstName}`;
    else return `Good evening, ${firstName}`;
  };

  // Function 1: Save emotion
  // Post emotion data to the server
  const fetchAndUpdateEmotionHistory = async (email) => {
    if (!email) return;
    try {
      const response = await fetch(`/api/emotions?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch emotions');
      const data = await response.json();
  
      // Update all relevant state
      setEmotionHistory(data.emotionHistory);
      setUniqueEmotions(new Set(data.emotionHistory.map(emo => emo.emotion_result)));
      return data.emotionHistory;
    } catch (error) {
      console.error('Error fetching emotions:', error);
      return [];
    }
  };

  const saveEmotion = async (emotionData, messagesToSave) => {
    try {
      const response = await fetch('/api/emotions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userInfo.email,
          emotionResult: emotionData,
          messages: messagesToSave,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        await fetchAndUpdateEmotionHistory(userInfo.email);
        
        const latestEmotion = {
          ...emotionData,
          conversationId: data.conversationId, 
        };

        setResultNumber(latestEmotion);
        setShowOverlay(true);
        setIsInitialConversationComplete(true); // Add this line
      }
    } catch (error) {
      console.error('Error saving emotion:', error);
    }
  };

  // Function 2: Restart
  const handleRestart = useCallback(() => {
    setShowFlagPopup(false);
    setShowPlusPopup(false);
    setIsLiked(false);

    // Clear chat and hide overlay
    setMessages([]);
    setResultNumber(null);
    setShowOverlay(false);

    // Refresh emotion history after any potential discards
    fetchAndUpdateEmotionHistory(userInfo.email);
  }, [userInfo.email]);

  const handleRestartKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      handleRestart();
    }
  }, [handleRestart]);

  const handleOverlayClose = () => {
    if (isOverlayFromHistory) {
      setShowOverlay(false);
      setResultNumber(null);
    } else {
      handleRestart();
    }
    // Close any open popups
    setShowFlagPopup(false);
    setShowPlusPopup(false);
    setIsOverlayFromHistory(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // If any popup is open, don't handle Escape here
        if (showFlagPopup || showPlusPopup) {
          return;
        }
        // Only handle Escape for overlay when no popups are open
        if (showOverlay) {
          e.stopPropagation(); // Prevent event bubbling
          handleOverlayClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFlagPopup, showPlusPopup, showOverlay, isOverlayFromHistory]);

  useEffect(() => {
    if (resultNumber) {
      setShowOverlay(true);
    }
  }, [resultNumber]);

  useEffect(() => {
    if (userInfo?.email) {
      fetchAndUpdateEmotionHistory(userInfo.email)
        .finally(() => setIsLoadingEmotions(false));
    }
  }, [userInfo?.email]);

  const generateShapeStyle = (emotionId, size = 60, isDiscarded = false) => {
    let color;
    let animationClass;
    
    if (emotionId >= 1 && emotionId <= 55) {
      color = isDiscarded ? 'var(--gray-red)' : 'var(--pastel-red)';
      animationClass = 'animate-high-unpleasant';
    } else if (emotionId >= 56 && emotionId <= 110) {
      color = isDiscarded ? 'var(--gray-yellow)' : 'var(--pastel-yellow)';
      animationClass = 'animate-high-pleasant';
    } else if (emotionId >= 111 && emotionId <= 165) {
      color = isDiscarded ? 'var(--gray-blue)' : 'var(--pastel-blue)';
      animationClass = 'animate-low-unpleasant';
    } else {
      color = isDiscarded ? 'var(--gray-purple)' : 'var(--pastel-purple)';
      animationClass = 'animate-low-pleasant';
    }
  
    const borderRadius = `${(emotionId * 7) % 100}% ${(emotionId * 11) % 100}% ${(emotionId * 13) % 100}% ${(emotionId * 17) % 100}%`;
  
    return {
      style: {
        backgroundColor: color,
        borderRadius: borderRadius,
        width: `${size}px`,
        height: `${size}px`,
        filter: isDiscarded ? 'grayscale(1)' : 'none'
      },
      animationClass
    };
  };

  const proceedWithDeletion = (index) => {
    if (index < 0 || index >= emotionHistory.length) {
      console.error('Invalid index for emotion deletion');
      return;
    }

    const emotionToDelete = emotionHistory[index];
    const element = document.querySelector(`[data-index="${index}"]`);
    
    if (element) {
      const wrapper = element.closest('.shape-wrapper');
      if (wrapper) {
        element.classList.add('deleting');
        
        setTimeout(() => {
          const newHistory = emotionHistory.filter((_, i) => i !== index);
          setEmotionHistory(newHistory);
          const uniqueIds = new Set(newHistory.map(emotion => emotion.emotion_result));
          setUniqueEmotions(uniqueIds);
          
          setShouldAnimateCounter(true);
          setTimeout(() => {
            setShouldAnimateCounter(false);
          }, 2000);
        }, 500);
      }
    }

    deleteEmotionFromDatabase(emotionToDelete.conversationId);
  };

  const handleDeleteEmotion = async (index, e) => {
    e.stopPropagation();
    
    const hasSeenWarning = localStorage.getItem('hasSeenDeletionWarning');
    
    if (!hasSeenWarning) {
      setEmotionToDelete(index);
      setShowDeletionWarning(true);
      return;
    }
    
    proceedWithDeletion(index);
  };

  const handleWarningConfirm = () => {
    localStorage.setItem('hasSeenDeletionWarning', 'true');
    setShowDeletionWarning(false);
  };

  const deleteEmotionFromDatabase = async (conversationId) => {
    try {
      const response = await fetch('/api/conversations?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userInfo.email,
          conversationId: conversationId,
        }),
      });
  
      if (!response.ok) {
        console.error('Failed to delete emotion from server');
      }
    } catch (error) {
      console.error('Error deleting emotion from server:', error);
    }
  };

  const handleHeartClick = async () => {
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);
    try {
      const response = await fetch('/api/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: resultNumber.conversationId,
          liked: newLikedState,
        }),
      });
      if (!response.ok) {
        console.error('Failed to send feedback');
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  };

  const handleFlagClick = () => {
    setShowPlusPopup(false);
    setShowFlagPopup(true);
  };

  const handlePlusClick = () => {
    setShowFlagPopup(false);
    setShowPlusPopup(true);
  };

  const submitFlagFeedback = async () => {
    try {
      const response = await fetch('/api/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: resultNumber.conversationId,
          liked: isLiked,
          flagFeedback: flagFeedback,
        }),
      });
      if (!response.ok) {
        console.error('Failed to send feedback');
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
    setShowFlagPopup(false);
    setFlagFeedback('');
  };

  const submitPlusFeedback = async () => {
    try {
      const response = await fetch('/api/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: resultNumber.conversationId,
          liked: isLiked,
          suggestedFeelingName: plusFeedback.name,
          suggestedFeelingDescription: plusFeedback.description,
        }),
      });
      if (!response.ok) {
        console.error('Failed to send feedback');
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
    setShowPlusPopup(false);
    setPlusFeedback({ name: '', description: '' });
  };

  const checkFeedbackStatus = async (conversationId) => {
    try {
      const response = await fetch(`/api/conversations?conversationId=${conversationId}`, {
        method: 'GET'
      });
      const data = await response.json();
      setIsLiked(data.liked || false);
    } catch (error) {
      console.error('Error checking feedback status:', error);
      setIsLiked(false);
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay background, not its contents
    if (e.target.className === 'results-overlay') {
      handleOverlayClose();
    }
  };

  const handleShapeClick = (index, emotion) => {
    if (highlightedShape === index) {
      setHighlightedShape(null);
    } else {
      setHighlightedShape(index);
    }
  };

  const handleShapeDoubleClick = (emotion) => {
    console.log('Emotion object:', emotion);
    // Find the complete emotion details from our emotions list
    const emotionDetails = emotions.find(e => e.id === emotion.emotion_result);
    
    if (emotionDetails) {
      setResultNumber({
        id: emotion.emotion_result,
        name: emotionDetails.name,
        description: emotionDetails.description,
        conversationId: emotion.conversation_id,
        category: emotionDetails.category
      });
      setIsOverlayFromHistory(true);
      setShowOverlay(true);
    }
  };

  const handleTouchStart = (index, emotion, e) => {
    e.preventDefault(); // Prevent default touch behavior
    
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // ms
    
    if (lastTap && (now - lastTap) < DOUBLE_TAP_DELAY) {
      // Double tap detected
      clearTimeout(touchTimer);
      setTouchTimer(null);
      handleShapeDoubleClick(emotion);
    } else {
      // Single tap
      const timer = setTimeout(() => {
        handleShapeClick(index, emotion);
      }, DOUBLE_TAP_DELAY);
      
      setTouchTimer(timer);
    }
    
    setLastTap(now);
  };
  
  const handleTouchEnd = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    const handleDocumentClick = (e) => {
      // If the click target is not inside any minimized-shape element,
      // clear the highlighted shape.
      if (!e.target.closest('.minimized-shape')) {
        setHighlightedShape(null);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return (
    <div className="chatbot-page">
      {showConfetti && (
        <ReactConfetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={200}
        />
      )}
      {showOverlay && resultNumber && (
        <div className="results-overlay" onClick={handleOverlayClick}>
          <div className="results-content" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const resultShape = generateShapeStyle(resultNumber.id, 200);
              return (
                <div
                  className={`emotion-shape-large ${resultShape.animationClass}`}
                  style={resultShape.style}
                />
              );
            })()}
            <div className="emotion-details">
              <h2 className="emotion-name">{resultNumber.name}</h2>
              <p className="emotion-description">{resultNumber.description}</p>
            </div>

            <div className="feedback-icons">
              <div className="feedback-icon" data-title="I wish to flag a response.">
                <FlagIcon className="flag-icon" 
                onClick={handleFlagClick} 
                />
              </div>
              <div className="feedback-icon" data-title="I love feelr's response!">
                <HeartIcon 
                  className={`heart-icon ${isLiked ? 'liked' : ''}`} 
                  onClick={handleHeartClick} 
                />
              </div>
              <div className="feedback-icon" data-title="I wish to suggest a feeling.">
                <PlusIcon className="plus-icon" 
                onClick={handlePlusClick} />
              </div>
            </div>
            <div className="restart-buttons-group">
              <button
                className="restart-button"
                onClick={() => {
                  // Save emotion and restart
                  handleOverlayClose();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    handleOverlayClose();
                  }
                }}
                tabIndex={0}
                autoFocus
              >
                {isOverlayFromHistory ? 'Close' : 'Another feeling to share?'}
              </button>
              
              {!isOverlayFromHistory && (
                <button
                  className="restart-button secondary"
                  onClick={async () => {
                    try {
                      // 1. Submit feedback that this was discarded
                      const response = await fetch('/api/conversations', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          conversationId: resultNumber.conversationId,
                          flagFeedback: 'discarded'
                        }),
                      });

                      if (!response.ok) {
                        throw new Error('Failed to record discarded feedback');
                      }

                      // 2. Refresh emotion history to get updated discarded states
                      await fetchAndUpdateEmotionHistory(userInfo.email);

                      // 3. Reset UI state
                      setShowOverlay(false);
                      setMessages([]);
                      setResultNumber(null);

                    } catch (error) {
                      console.error('Error handling discard:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') {
                      setShowOverlay(false);
                      setMessages([]);
                      setResultNumber(null);
                    }
                  }}
                  tabIndex={0}
                >
                  Not a match? Try again!
                </button>
              )}
            </div>

            {showFlagPopup && (
              <Popup onClose={() => setShowFlagPopup(false)}>
                <textarea
                  maxLength="140"
                  value={flagFeedback}
                  onChange={(e) => setFlagFeedback(e.target.value)}
                  style={{ height: '144px' }}
                  placeholder="Got feedback? Type here!"
                />
                <button onClick={submitFlagFeedback}>Submit</button>
                <button onClick={() => setShowFlagPopup(false)}>Cancel</button>
              </Popup>
            )}

            {showPlusPopup && (
              <Popup onClose={() => setShowPlusPopup(false)}>
                <input
                  type="text"
                  value={plusFeedback.name || ''}
                  onChange={(e) => setPlusFeedback({ ...plusFeedback, name: e.target.value })}
                  placeholder="Name of the feeling"
                />
                <textarea
                  maxLength="140"
                  value={plusFeedback.description || ''}
                  onChange={(e) => setPlusFeedback({ ...plusFeedback, description: e.target.value })}
                  placeholder="Describe this feeling..."
                />
                <button onClick={submitPlusFeedback}>Submit</button>
                <button onClick={() => setShowPlusPopup(false)}>Cancel</button>
              </Popup>
            )}
          </div>
        </div>
      )}

      {showDeletionWarning && (
        <div className="popup">
          <p>Erasing a feeling shape wipes it and the conversation that led to it from the database.</p>
          <button 
            onClick={handleWarningConfirm}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleWarningConfirm();
              }
            }}
            autoFocus
          >
            I understand!
          </button>
        </div>
      )}

      {!showOverlay && (
        <>
          <h1 className="greet">{getGreeting()}</h1>
          <AnimatedQuestion />
          <ClaudeChat
            userInfo={userInfo}
            onEmotionResult={saveEmotion}
            messages={messages}
            setMessages={setMessages}
            emotionHistory={emotionHistory} // Add this prop
          />
        </>
      )}

      {emotionHistory.length > 0 && isInitialConversationComplete && (
        <>
          <div className="flex items-center justify-center mb-4">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={showDiscarded}
                onChange={(e) => setShowDiscarded(e.target.checked)}
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-pastel-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pastel-blue-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-600">Show Discarded</span>
            </label>
          </div>

          <div className="minimized-shapes relative w-full max-w-[600px] mx-auto my-8 hidden grid-cols-[repeat(auto-fill,minmax(60px,1fr))] justify-center gap-5 p-5 max-h-[280px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300 box-border sm:max-w-full sm:p-2.5 sm:gap-2.5 sm:grid-cols-[repeat(auto-fill,minmax(45px,1fr))]">
            {emotionHistory
              .filter(emotion => !emotion.isDiscarded || showDiscarded)
              .map((emotion, index) => {
                const shapeData = generateShapeStyle(
                  emotion.emotion_result, 
                  60, 
                  emotion.isDiscarded
                );
                const uniqueKey = emotion.conversationId || emotion.emotion_result;
                return (
                  <div 
                    key={uniqueKey}
                    className="shape-wrapper relative inline-flex p-2 max-w-[70px] max-h-[70px] m-0 sm:max-w-[60px] sm:max-h-[60px]"
                  >
                    <div
                      className={`minimized-shape ${shapeData.animationClass} ${
                        highlightedShape === index ? 'highlighted' : ''
                      } ${emotion.isDiscarded ? 'discarded' : ''}`}
                      style={shapeData.style}
                      data-index={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShapeClick(index, emotion);
                      }}
                      onDoubleClick={() => handleShapeDoubleClick(emotion)}
                      onTouchStart={(e) => handleTouchStart(index, emotion, e)}
                      onTouchEnd={handleTouchEnd}
                    />
                    <button
                      className="delete-button absolute -top-2.5 -right-4 bg-transparent text-[#5c5c5c] hover:text-[#333] border-none rounded-full text-sm w-6 h-6 cursor-pointer z-10 transition-all duration-200 flex items-center justify-center hover:scale-110"
                      onClick={(e) => handleDeleteEmotion(index, e)}
                    >×</button>
                  </div>
                );
              })}
          </div>
        </>
      )}
      <FaqButton />
    </div>
  );
};

export default ChatbotPage;
